import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime } from "../../utils/dateTime";
import "./AttachmentsCard.css";

const EMPTY = [];
const MB = 1024 * 1024;

const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= MB) return `${(n / MB).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};
const fileIcon = (mime = "", name = "") => {
  if (mime.startsWith("image/")) return "bi-file-earmark-image";
  if (mime === "application/pdf" || /\.pdf$/i.test(name))
    return "bi-file-earmark-pdf";
  if (/sheet|excel|csv/i.test(mime) || /\.(xlsx?|csv)$/i.test(name))
    return "bi-file-earmark-spreadsheet";
  return "bi-file-earmark";
};
const uploaderName = (u) =>
  typeof u === "string"
    ? u
    : u?.name ||
      [u?.firstName, u?.lastName].filter(Boolean).join(" ") ||
      u?.email ||
      null;

/**
 * Files attached to a record (asset documents, seminar sheets/photos):
 * list with open (new tab) / delete, and an upload form (category, remarks, file).
 * categories = [{ value, label }]; onUpload({ category, remarks, file }) and
 * onDelete(item) return promises. 400 messages from the server are shown as is.
 */
const AttachmentsCard = ({
  title = "Documents",
  items = EMPTY,
  isLoading,
  error,
  categories,
  defaultCategory,
  accept,
  acceptLabel,
  maxMb = 10,
  canModify,
  onUpload,
  onDelete,
  emptyText = "No files yet.",
  className = "",
}) => {
  const fileRef = useRef(null);
  const [category, setCategory] = useState(
    defaultCategory || categories[0]?.value,
  );
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const labelOf = (v) => categories.find((c) => c.value === v)?.label || v;

  const pick = (f) => {
    setFormError(null);
    if (f && f.size > maxMb * MB) {
      setFormError(`The file is larger than ${maxMb} MB.`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFile(f || null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setFormError("Choose a file.");
    setBusy(true);
    setFormError(null);
    try {
      await onUpload({ category, remarks: remarks.trim() || undefined, file });
      toast.success(`${file.name} uploaded.`);
      setFile(null);
      setRemarks("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      const m = err?.response?.data?.message;
      setFormError(
        Array.isArray(m)
          ? m.join(" ")
          : getApiErrorMessage(err, "Could not upload the file."),
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.fileName}?`)) return;
    setDeletingId(item.id);
    try {
      await onDelete(item);
      toast.success("File deleted.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not delete the file."));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className={`att-files ${className}`}>
      <h2 className="att-files-title">
        {title}
        {items.length > 0 && (
          <span className="att-files-count">{items.length}</span>
        )}
      </h2>

      {isLoading && (
        <div className="text-center py-3">
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      )}
      {error && (
        <div className="alert alert-danger fs-080 py-2">
          {getApiErrorMessage(error, "Could not load the files.")}
        </div>
      )}
      {!isLoading && !error && items.length === 0 && (
        <p className="fs-080 text-slate-500 mb-2">{emptyText}</p>
      )}

      {items.length > 0 && (
        <ul className="att-files-list">
          {items.map((d) => (
            <li key={d.id}>
              <i
                className={`bi ${fileIcon(d.mimeType, d.fileName)} att-files-icon`}
                aria-hidden="true"
              ></i>
              <div className="att-files-body">
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="att-files-name"
                  title="Open in a new tab"
                >
                  {d.fileName}
                  <i className="bi bi-box-arrow-up-right ms-1 fs-070"></i>
                </a>
                <div className="att-files-meta">
                  <span className="chip chip--sm chip--alt">
                    {labelOf(d.category)}
                  </span>
                  {d.size ? <span>{formatSize(d.size)}</span> : null}
                  {uploaderName(d.uploadedBy) && (
                    <span>{uploaderName(d.uploadedBy)}</span>
                  )}
                  <span>{formatDateTime(d.createdAt)}</span>
                </div>
                {d.remarks && (
                  <div className="att-files-remarks">{d.remarks}</div>
                )}
              </div>
              {canModify && (
                <button
                  type="button"
                  className="btn btn-sm btn-light border text-danger"
                  onClick={() => remove(d)}
                  disabled={deletingId === d.id}
                  aria-label={`Delete ${d.fileName}`}
                >
                  {deletingId === d.id ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    <i className="bi bi-trash"></i>
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canModify && (
        <form className="att-files-form" onSubmit={submit}>
          {formError && (
            <div className="alert alert-danger fs-080 py-2 mb-2">
              {formError}
            </div>
          )}
          <div className="row g-2">
            <div className="col-sm-5">
              <select
                className="form-select form-select-sm"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                aria-label="Category"
                disabled={busy}
              >
                {categories.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-7">
              <input
                className="form-control form-control-sm"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Remarks (optional)"
                maxLength={500}
                disabled={busy}
                aria-label="Remarks"
              />
            </div>
            <div className="col-12 d-flex flex-wrap gap-2 align-items-center">
              <input
                ref={fileRef}
                type="file"
                className="form-control form-control-sm att-files-input"
                accept={accept}
                onChange={(e) => pick(e.target.files?.[0])}
                disabled={busy}
                aria-label="File"
              />
              <button
                type="submit"
                className="btn btn-sm btn-primary"
                disabled={busy || !file}
              >
                {busy && (
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    aria-hidden="true"
                  ></span>
                )}
                <i className="bi bi-upload me-1"></i>Upload
              </button>
            </div>
          </div>
          <div className="fs-070 text-slate-500 mt-1">
            {acceptLabel ? `${acceptLabel}, ` : ""}max {maxMb} MB.
          </div>
        </form>
      )}
    </div>
  );
};

export default AttachmentsCard;
